import type { ErpConversionItem, ErpConversionKind } from "../api/hooks";

/**
 * Utilidades de la exportación al ERP. La API es quien valida de verdad; esto evita ir y volver
 * al servidor con errores evidentes y traduce los códigos internos a frases para la persona que opera.
 */

/** Límites de los campos del ERP que se configuran en la tabla de conversión. */
export const ERP_LIMITS = { article: 13, paymentMethod: 3, warehouse: 3, agentDigits: 5 } as const;

export const CONVERSION_LABELS: Record<ErpConversionKind, Record<string, string>> = {
  ARTICLE: {
    base: "Cuota base del plan",
    driver: "Conductores activos",
    cargador: "Cargadores",
    deca_usage: "Uso de DeCA (por documento)",
    discount: "Descuento",
    other: "Otros conceptos / servicio genérico",
  },
  PAYMENT_METHOD: {
    STRIPE_CARD: "Tarjeta (Stripe)",
    STRIPE_SEPA_DEBIT: "Adeudo SEPA (Stripe)",
    STRIPE_BIZUM: "Bizum (Stripe)",
    BANK_TRANSFER_MANUAL: "Transferencia manual",
  },
  SETTING: {
    warehouse: "Almacén (se aplica a todas las líneas)",
    agent: "Agente (se aplica a todas las facturas)",
  },
};

export const CONVERSION_SECTIONS: Array<{ kind: ErpConversionKind; title: string; hint: string }> = [
  {
    kind: "ARTICLE",
    title: "Artículos",
    hint: `Código de artículo del ERP para cada concepto de la factura (hasta ${ERP_LIMITS.article} caracteres). En blanco = la línea se exporta sin código de artículo.`,
  },
  {
    kind: "PAYMENT_METHOD",
    title: "Formas de pago",
    hint: `Código de forma de pago del ERP según cómo cobró el cliente (hasta ${ERP_LIMITS.paymentMethod} caracteres).`,
  },
  {
    kind: "SETTING",
    title: "Valores generales",
    hint: "Se escriben igual en todas las facturas. Pueden quedar en blanco.",
  },
];

/** Mensaje de error de un código de conversión, o null si es válido. Vacío siempre es válido. */
export function validateConversionCode(item: ErpConversionItem): string | null {
  const code = item.erpCode.trim();
  if (code === "") return null;
  if (item.kind === "ARTICLE" && code.length > ERP_LIMITS.article) {
    return `Máximo ${ERP_LIMITS.article} caracteres`;
  }
  if (item.kind === "PAYMENT_METHOD" && code.length > ERP_LIMITS.paymentMethod) {
    return `Máximo ${ERP_LIMITS.paymentMethod} caracteres`;
  }
  if (item.kind === "SETTING" && item.key === "warehouse" && code.length > ERP_LIMITS.warehouse) {
    return `Máximo ${ERP_LIMITS.warehouse} caracteres`;
  }
  if (item.kind === "SETTING" && item.key === "agent" && !/^[0-9]{1,5}$/.test(code)) {
    return `Solo números, hasta ${ERP_LIMITS.agentDigits} cifras`;
  }
  return null;
}

/** Solo las filas cuyo código ha cambiado respecto al guardado (el servidor solo audita lo que cambia). */
export function changedConversions(saved: ErpConversionItem[], draft: ErpConversionItem[]): ErpConversionItem[] {
  const before = new Map(saved.map((item) => [`${item.kind}:${item.key}`, item.erpCode]));
  return draft
    .map((item) => ({ ...item, erpCode: item.erpCode.trim() }))
    .filter((item) => before.get(`${item.kind}:${item.key}`) !== item.erpCode);
}

const BLOCK_REASONS: Record<string, string> = {
  incomplete_fiscal_data: "El cliente tiene datos fiscales incompletos",
  older_unnumbered: "Hay una factura anterior sin numerar",
  out_of_order: "La numeración quedaría fuera de orden de cobro",
  lines_mismatch: "Las líneas de la factura no suman su importe neto",
  series_invalid: "La serie de facturas no es válida para el ERP",
  no_settings: "Faltan los ajustes de facturación",
  counter_overflow: "Se agotó el contador de la serie",
  not_eligible: "La factura no cumple las condiciones para numerarse",
  provider_check_failed: "No se pudo consultar la factura en Stripe",
  billing_not_configured: "Stripe no está configurado",
};

export function describeBlockReason(reason: string): string {
  return BLOCK_REASONS[reason] ?? `Motivo desconocido (${reason})`;
}

const pad = (value: number) => String(value).padStart(2, "0");
const iso = (date: Date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

/** Mes natural anterior a `now` (primer y último día): el rango habitual de una exportación mensual. */
export function previousMonthRange(now: Date = new Date()): { from: string; to: string } {
  const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { from: iso(first), to: iso(last) };
}

/** `AAAA-MM-DD` → `DD/MM/AAAA`. */
export function formatDay(value: string): string {
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
}

export function formatEuro(value: number): string {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(value);
}

/** Nombre del fichero descargado: lo que manda la API no llega al cliente (solo el blob), se rehace aquí. */
export function batchFilename(batch: { id: string; from: string; to: string; isReexport: boolean }): string {
  return `erp_${batch.from}_${batch.to}${batch.isReexport ? "_reexport" : ""}_${batch.id.slice(0, 8)}.zip`;
}

/** Problemas agrupados por factura, para listarlos de forma legible. */
export function groupProblems<T extends { invoiceId: string }>(problems: T[]): Array<{ invoiceId: string; items: T[] }> {
  const groups = new Map<string, T[]>();
  for (const problem of problems) {
    groups.set(problem.invoiceId, [...(groups.get(problem.invoiceId) ?? []), problem]);
  }
  return [...groups.entries()].map(([invoiceId, items]) => ({ invoiceId, items }));
}

/** Un dígito, que es lo que admite el campo serie del ERP. */
export function validateSeriesCode(value: string): string | null {
  return /^[0-9]$/.test(value.trim()) ? null : "Un único dígito, de 0 a 9";
}

/** Próximo número: entero entre el mínimo permitido (no repetir lo ya asignado) y el máximo de la serie. */
export function validateNextNumber(value: string, min: number, max: number): string | null {
  const text = value.trim();
  if (!/^[0-9]+$/.test(text)) return "Solo números enteros";
  const number = Number(text);
  if (number < min) return min > 1 ? `Debe ser ${min} o mayor: ya se asignaron números hasta el ${min - 1}` : "Debe ser 1 o mayor";
  if (number > max) return `Como máximo ${max} (6 dígitos)`;
  return null;
}

/** Número tal y como saldrá en la factura: serie + 6 dígitos. */
export function formatInvoiceNumber(code: string, number: number): string {
  return `${code}-${String(number).padStart(6, "0")}`;
}

/** Texto de la paginación: «1–50 de 120», o «0 facturas» si no hay ninguna. */
export function describeInvoicePage(offset: number, shown: number, total: number): string {
  if (total === 0 || shown === 0) return "0 facturas";
  return `${offset + 1}–${offset + shown} de ${total}`;
}

/** Página anterior y siguiente de la lista, o `null` si no existen. */
export function neighbourOffsets(offset: number, limit: number, total: number): { prev: number | null; next: number | null } {
  return {
    prev: offset > 0 ? Math.max(0, offset - limit) : null,
    next: offset + limit < total ? offset + limit : null,
  };
}
